import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of, Subject, Subscription } from 'rxjs';
import { bufferCount, catchError, concatMap, first, tap } from 'rxjs/operators';

import { HttpBasicRequest, HttpBasicRequestOptions, HttpBasicTextRequestOptions } from '../models/http-basic-request.model';
import { LoaderService } from './loader.service';
import { UtilityFunctions } from '../utilities/functions.models';

export class BasicRequestHandler {
  public loaderId: string;
  public subscription: Subscription;
  public running?: boolean;
  constructor(init?: Partial<BasicRequestHandler>) {
    Object.assign(this, init);
  }
}

@Injectable()
export class BasicHttpClientService {

  public loaders: BasicRequestHandler[] = [];

  constructor(
    private httpClient: HttpClient,
    private loaderService: LoaderService
  ) { }

  public get<T>(
    url: string,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<T> {
    this.loaderService.showLoader(loaderId);

    let req = this.loaders.find(x => x.loaderId === loaderId);
    if (!!req && req.running) req.subscription.unsubscribe();
    else req = this.loaders[this.loaders.push(new BasicRequestHandler({ loaderId, running: true })) - 1];

    const subject = new Subject<T>();
    req.subscription = this.httpClient.get<T>(url, options || new HttpBasicRequestOptions())
      .subscribe(
        data => {
          req.running = false;
          this.loaderService.dismissLoader(loaderId);
          subject.next(data);
        },
        error => {
          req.running = false;
          this.loaderService.dismissLoader(loaderId);
          console.error(error);
          subject.error(error);
        });
    return subject.asObservable().pipe(first());
  }

  public getBlob(
    url: string,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ) {
    this.loaderService.showLoader(loaderId);

    let req = this.loaders.find(x => x.loaderId === loaderId);
    if (!!req && req.running) req.subscription.unsubscribe();
    else req = this.loaders[this.loaders.push(new BasicRequestHandler({ loaderId, running: true })) - 1];

    const subject = new Subject<{ blob: Blob; fileName: string; }>();
    req.subscription = this.httpClient.get(url, {
      responseType: 'blob',
      headers: options?.headers,
      observe: 'response',
      params: options?.params
    }).subscribe(
      data => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        const filename = UtilityFunctions.getFilenameFromContentDisposition(data.headers.get('Content-Disposition'));
        subject.next({ blob: data.body, fileName: filename });
      },
      error => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        console.error(error);
        subject.error(error);
      });
    return subject.asObservable();
  }

  public post<T>(
    url: string,
    body: any,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<any> {
    this.loaderService.showLoader(loaderId);

    let req = this.loaders.find(x => x.loaderId === loaderId);
    if (!!req && req.running) req.subscription.unsubscribe();
    else req = this.loaders[this.loaders.push(new BasicRequestHandler({ loaderId, running: true })) - 1];

    const subject = new Subject();
    req.subscription = this.httpClient.post<T>(url, body, options || new HttpBasicRequestOptions()).subscribe(
      data => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        subject.next(data);
      },
      error => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        console.error(error);
        subject.error(error);
      });
    return subject.asObservable();
  }

  public postTextResponse(
    url: string,
    body: any,
    options?: HttpBasicTextRequestOptions,
    loaderId?: string
  ): Observable<string> {
    this.loaderService.showLoader(loaderId);

    let req = this.loaders.find(x => x.loaderId === loaderId);
    if (!!req && req.running) req.subscription.unsubscribe();
    else req = this.loaders[this.loaders.push(new BasicRequestHandler({ loaderId, running: true })) - 1];

    const subject = new Subject<string>();
    req.subscription = this.httpClient.post(url, body, options || new HttpBasicTextRequestOptions()).subscribe(
      data => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        subject.next(data);
      },
      error => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        console.error(error);
        subject.error(error);
      });
    return subject.asObservable();
  }

  public put<T>(
    url: string,
    body: any,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<any> {
    this.loaderService.showLoader(loaderId);

    let req = this.loaders.find(x => x.loaderId === loaderId);
    if (!!req && req.running) req.subscription.unsubscribe();
    else req = this.loaders[this.loaders.push(new BasicRequestHandler({ loaderId, running: true })) - 1];

    const subject = new Subject();
    req.subscription = this.httpClient.put<T>(url, body, options || new HttpBasicRequestOptions()).subscribe(
      data => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        subject.next(data);
      },
      error => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        console.error(error);
        subject.error(error);
      });
    return subject.asObservable();
  }

  public delete<T>(
    url: string,
    options?: HttpBasicRequestOptions,
    loaderId?: string
  ): Observable<any> {
    this.loaderService.showLoader(loaderId);

    let req = this.loaders.find(x => x.loaderId === loaderId);
    if (!!req && req.running) req.subscription.unsubscribe();
    else req = this.loaders[this.loaders.push(new BasicRequestHandler({ loaderId, running: true })) - 1];

    const subject = new Subject();
    req.subscription = this.httpClient.delete<T>(url, options || new HttpBasicRequestOptions()).subscribe(
      data => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        subject.next(data);
      },
      error => {
        req.running = false;
        this.loaderService.dismissLoader(loaderId);
        console.error(error);
        subject.error(error);
      });
    return subject.asObservable();
  }

  public forkJoin<T>(requests: HttpBasicRequest[], loaderId?: string, buffCount: number = 100): Observable<T[]> {
    requests = requests.filter(x => !!x?.method);
    const sbj = new Subject<T[]>();
    if (requests?.length) {
      if (loaderId) this.loaderService.showLoader(loaderId);

      let req = this.loaders.find(x => x.loaderId === loaderId);
      if (!!req && req.running) req.subscription.unsubscribe();
      else {
        if (loaderId) req = this.loaders[this.loaders.push(new BasicRequestHandler({ loaderId, running: true })) - 1];
        else req = new BasicRequestHandler({ loaderId });
      }

      const sources = [];
      requests.forEach(r => {
        if (r.method == 'get' || r.method == 'delete')
          sources.push(
            this.httpClient[r.method](r.url, r.options || new HttpBasicRequestOptions()).pipe(
              catchError(error => {
                console.error(error);
                return of(null);
              })
            )
          );
        if (r.method == 'post' || r.method == 'put')
          sources.push(
            this.httpClient[r.method](r.url, r.body || null, r.options || new HttpBasicRequestOptions()).pipe(
              tap({
                next: data => data,
                error: (error: HttpErrorResponse) => {
                  console.error(error);
                }
              })
            )
          );
      });
      const result = [];
      req.subscription = from(sources).pipe(
        bufferCount(buffCount),
        concatMap(tasks => {
          return forkJoin([...tasks]);
        })).subscribe(res => {
          result.push(...res);
          if (sources.length === result.length) {
            req.running = false;
            if (loaderId) this.loaderService.dismissLoader(loaderId);
            sbj.next(result);
          }
        }, (error: HttpErrorResponse) => {
          req.running = false;
          console.error(error);
          if (loaderId) this.loaderService.dismissLoader(loaderId);
        });
    }
    return sbj.asObservable();
  }

  public destroyLoader(loaderId: string) {
    const load = this.loaders.find(x => x.loaderId === loaderId);
    if (load && load.subscription) load.subscription.unsubscribe();
  }
}
